import { Circle, Group, Path, Rect, Text } from "react-konva";
import type { IGanttController } from "elements/gantt/controller";
import GanttHeaderDateCell from "elements/gantt/components/date-cell";
import Constants from "elements/gantt/constants";
import type { GanttElement } from "elements/gantt/schema";
import type { ElementProps } from "elements/base/provider";
import { RefObject, useCallback, useEffect, useRef, useState } from "react";
import Konva from "konva";
import {
  getCornerRadius,
  getMondayItemId,
  hasDateConfigInMetadata,
  hasUserSeenGanttGuestUserError,
  isMondayGanttElement,
  isTargetAnchor,
  isTargetConnector,
  isTargetSplitCell,
  isTargetTaskCell,
  setUserHasSeenGanttGuestUserError,
  trackGanttEvent,
} from "elements/gantt/utils";
import GanttTaskComponent from "elements/gantt/components/task-cell";
import { useEvent } from "react-use";
import { EVT_ELEMENT_DRAG, EVT_ELEMENT_DROP } from "../../canvas-designer-new/elements/card-stack/card-stack-utils";
import { isPointInRect } from "utils/math-utils";
import useObservableController from "elements/hooks/use-observable-controller";
import consts from "shared/consts";
import { resetPointer, setMouseDrag, setMousePointer, unsetMouse } from "utils/mouse-cursor-utils";
import { useAtom, useAtomValue, useSetAtom } from "jotai";
import {
  focusedElementIdAtom,
  internalSelectionAtom,
  selectedElementIdsAtom,
  mondayIntegrationPopupAtom,
  transformerRefAtom,
  mouseStateAtom,
  stageRefAtom,
  userAtom,
} from "state-atoms";
import { Degrees } from "utils/transform";
import { EmptyStateTasks } from "elements/gantt/components/empty-state-tasks";
import { AddActions } from "elements/gantt/components/add-actions/add-actions";
import KonvaTooltip from "frontend/canvas-designer-new/utility-elements/konva-tooltip";
import { cleanDate, formatRelatedDates } from "shared/util/date-utils";
import GanttSplitColumn from "elements/gantt/components/split-column";
import { CanvasKeyboardShortcut, useKeyboardShortcuts } from "utils/keyboard-shortcuts";
import SimpleConnector from "frontend/canvas-designer-new/elements/connector/simple-connector";
import AddButton from "frontend/canvas-designer-new/utility-elements/add-button";
import { IGanttSplitCellController } from "elements/gantt/controllers/split-cell-controller";
import { usePusher } from "frontend/hooks/use-pusher";
import { getFeatureFlag } from "frontend/hooks/use-feature-flag/use-feature-flag";
import useDebounceCallback from "frontend/hooks/use-debounce-callback";
import { useGanttMaxTasks } from "elements/gantt/hooks/gantt-max-tasks";
import { useBoardTasks } from "frontend/hooks/use-board-integrations";
import { IGanttBaseCellController } from "elements/gantt/controllers/base-cell-controller";
import { IntegrationItem } from "shared/datamodel/schemas";
import { GanttConnectorWrapper } from "elements/gantt/components/gantt-connector/gantt-connector-wrapper";
import { useGanttCustomRows } from "elements/gantt/hooks/gantt-use-custom-rows";
import { useGanttSyncMondayItems } from "elements/gantt/hooks/gantt-sync-monday-items";
import { Html } from "react-konva-utils";
import { RemoveMondayConnectorPopup } from "elements/gantt/components/remove-monday-connector-popup/remove-monday-connector-popup";
import Modal from "frontend/modal/modal";
import { ConnectorPopup } from "elements/gantt/components/connector-popup/connector-popup";
import { useIsPresenting } from "frontend/hooks/use-is-presenting";
import { GuestUserError } from "elements/gantt/components/guest-user-error";
import { addDays } from "date-fns";
import { useColumnConfiguration } from "elements/gantt/hooks/use-column-configuration";
import { GanttConnectorLayout } from "elements/gantt/layout-builder";
import { showModalFromKonva } from "frontend/modal/modal-bridge";
import EditTaskModal from "elements/task/components/modal/edit-task-modal";
import {
  getElementTypeForId,
  isGanttSupportedElement,
} from "frontend/canvas-designer-new/elements/canvas-elements-utils";
import { GanttMondayItemElement } from "elements/gantt/types";

function isIdForMondayElement(id: string): boolean {
  return id.startsWith("integrationItem") || id.startsWith("mondayTask");
}

export default function GanttElementComponent({ controller: _controller }: ElementProps<GanttElement>) {
  // we cast the controller to GanttController to satisfy the type checker
  const controller = _controller as IGanttController;
  const transformerRef = useAtomValue(transformerRefAtom);
  const stage = useAtomValue(stageRefAtom);
  const setInternalTransformer = useSetAtom(internalSelectionAtom);
  const [mondayIntegrationPopup, setMondayIntegrationPopup] = useAtom(mondayIntegrationPopupAtom);
  const mouseState = useAtomValue(mouseStateAtom);
  const maxAllowedTasksInPlan = useGanttMaxTasks();
  const isPresenting = useIsPresenting();

  const taskCellRefs = useRef(
    new Map<
      string,
      {
        setIsHovering: (isHovering: boolean, e?: Konva.KonvaEventObject<MouseEvent>) => void;
        id: string;
        taskCellRef: RefObject<Konva.Rect>;
      }
    >()
  );

  const ref = useRef<Konva.Group>(null);
  // it's a string because creating an object of {splitId: string, rowId: string} is expensive
  const [isHoveringSplitRow, setIsHoveringSplitRow] = useState<string | null>(null);
  const [isHoveringTask, setIsHoveringTask] = useState<string | null>(null);
  const [ghostConnector, setGhostConnector] = useState<{
    from: { taskId?: string; x: number; y: number };
    to: { taskId?: string; x: number; y: number };
  } | null>(null);
  const [connectorPopup, setConnectorPopup] = useState<{
    x: number;
    y: number;
    changeToMondayConnector: () => void;
  } | null>(null);
  const [hoveringConnector, setHoveringConnector] = useState<"left" | "right" | null>(null);
  const [isDraggingTask, setIsDraggingTask] = useState(false);
  const currentDragInfo = useRef<null | { taskId: string; canAddNewTask: boolean }>(null);
  const pusherChannel = controller.context.pusherChannel;
  const setFocusedElementId = useSetAtom(focusedElementIdAtom);
  const [selectedElementIds, setSelectedElementIds] = useAtom(selectedElementIdsAtom);
  const user = useAtomValue(userAtom);
  const { updateItem, updateColumnValue, tasksIntegrations } = useBoardTasks(
    controller.context.documentId,
    pusherChannel
  );

  const findExistingMapping = useColumnConfiguration({
    reflect: controller.context.reflect,
  });
  const [showMondayDependenciesPopup, setShowMondayDependenciesPopup] = useState(false);
  const taskItemsToRemoveDependency = useRef(new Map<string, IGanttBaseCellController<GanttMondayItemElement>>());
  const fetchLoadingItemsRef = useRef(new Set<string>());

  const { updateTask, getIntegrationTaskDates } = useGanttSyncMondayItems({
    controller,
  });

  const { changeTaskColumnValue } = useGanttCustomRows({
    controller: controller,
  });

  // makes it so the component re-renders when the controller changes
  useObservableController(controller, undefined, () => {
    if (controller.isSelected()) {
      transformerRef?.current?.forceUpdate();
    }
  });

  const reportTaskDateChanged = useDebounceCallback((type: "taskCard" | "integrationItem" | "task" | "mondayTask") => {
    trackGanttEvent("gantt_task_edited", {
      from: "gantt",
      field: "date",
      type,
    });
  }, 700);

  const updateTaskDateChanged = useDebounceCallback((taskId: string) => {
    const task = controller
      .getTaskCells()
      .find((task) => task.id === taskId && task.element.type === consts.CANVAS_ELEMENTS.INTEGRATION) as
      | IGanttBaseCellController<GanttMondayItemElement>
      | undefined;
    if (task) {
      addItemsToQueue({ [task.element.integrationId]: [getMondayItemId(task.element)] });
    }
  }, 700);

  const { addItemsToQueue } = useBoardTasks(controller.context.documentId, pusherChannel);
  useEvent(EVT_ELEMENT_DRAG, (event) => {
    const taskIdsOnly = event.detail.ids.filter((id: string) => {
      const elementType = getElementTypeForId(id);
      if (!isGanttSupportedElement(elementType)) {
        return false;
      }

      if (elementType === consts.CANVAS_ELEMENTS.INTEGRATION && !getFeatureFlag("gantt-monday-integration-items")) {
        return false;
      }

      return true;
    });
    if (taskIdsOnly.length === 0 || event.detail.ids.length !== 1) {
      return;
    }

    const ganttBounds = controller.getLayoutRect();
    const internalPoint = {
      x: (event.detail.mousePosition.x - ganttBounds.x) / (controller.element.scaleX ?? 1),
      y: (event.detail.mousePosition.y - ganttBounds.y) / (controller.element.scaleY ?? 1),
    };
    handleDraggedTask(taskIdsOnly[0], internalPoint, event);
  });

  useEvent(EVT_ELEMENT_DROP, (event) => {
    const taskIdsOnly = (event.detail.ids ?? []).filter((id: string) => {
      const elementType = getElementTypeForId(id);
      if (!isGanttSupportedElement(elementType)) {
        return false;
      }

      if (elementType === consts.CANVAS_ELEMENTS.INTEGRATION && !getFeatureFlag("gantt-monday-integration-items")) {
        return false;
      }

      return true;
    });
    if (taskIdsOnly.length === 0) {
      return;
    }
    const ganttBounds = controller.getLayoutRect();
    const isDroppedHere = isPointInRect(event.detail, ganttBounds);
    if (!isDroppedHere) {
      return;
    }

    const internalPoint = {
      x: (event.detail.x - ganttBounds.x) / (controller.element.scaleX ?? 1),
      y: (event.detail.y - ganttBounds.y) / (controller.element.scaleY ?? 1),
    };

    handleDraggedTask(taskIdsOnly[0], internalPoint, event);
    currentDragInfo.current = null;
  });
  const shouldShowMondayIntegrationPopup = controller.getShowMondayIntegrationPopup();

  useEffect(() => {
    if (
      shouldShowMondayIntegrationPopup.isOpen &&
      shouldShowMondayIntegrationPopup.integrationId &&
      mouseState === "up"
    ) {
      setMondayIntegrationPopup({
        isOpen: true,
        integrationId: shouldShowMondayIntegrationPopup?.integrationId,
      });
    }
  }, [shouldShowMondayIntegrationPopup, mouseState]);

  useEffect(() => {
    if (!mondayIntegrationPopup.isOpen) {
      controller.showMondayIntegrationPopup({
        isOpen: false,
      });
    }
  }, [mondayIntegrationPopup]);

  useEffect(() => {
    if (maxAllowedTasksInPlan) {
      controller.setMaxAllowedTasksInPlan(maxAllowedTasksInPlan);
    }
  }, [controller, maxAllowedTasksInPlan]);

  useEffect(() => {
    if (
      !controller.isSelected() ||
      (!controller.getSelectedConnectorId() && !controller.getSelectedSplitId() && !controller.getSelectedRowId())
    ) {
      setInternalTransformer((previous) => previous.filter((id) => id !== controller.id));
    } else {
      setInternalTransformer((previous) => [...previous, controller.id]);
    }
  }, [controller.isSelected(), controller.getSelectedConnectorId(), controller.getSelectedSplitId()]);

  usePusher<{
    data: {
      itemId: number;
      columnValues?: {
        id: string;
        type: string;
        value:
          | string
          | {
              from: string;
              to: string;
            };
      }[];
      name: string;
    };
  }>(
    pusherChannel,
    ["integration_data_changed", "live_integration_data_changed"],
    async ({ data }) => {
      for (const task of controller.getTaskCells()) {
        const element = task.element;
        const itemId = getMondayItemId(element as GanttMondayItemElement);

        if (
          data &&
          data.itemId &&
          (data.columnValues || data.name) &&
          isMondayGanttElement(element) &&
          itemId?.toString() === data?.itemId.toString()
        ) {
          const updateResult = await task?.updateIntegration?.("pusher", data);
          if (!updateResult?.didUpdate && !fetchLoadingItemsRef.current.has(task.id)) {
            fetchLoadingItemsRef.current.add(task.id);
            setTimeout(async () => {
              await updateTask(task.id);
              fetchLoadingItemsRef.current.delete(task.id);
            }, Constants.MondayIntegrationSyncIntervalSeconds * 1000);
          }
        }
      }
    },
    [controller]
  );

  useEffect(() => {
    if (selectedElementIds.length > 0) {
      hoverTaskCell(selectedElementIds[0], true);
    }
  }, [selectedElementIds]);

  const removeConnector = useCallback(
    async (connector: GanttConnectorLayout) => {
      const mondayItemsTasksForConnectors = controller
        .getTaskCells()
        .filter(
          (task) =>
            (task.id === connector.from.id && isMondayGanttElement(task.element) && task.isIntegrated()) ||
            (task.id === connector.to.id && isMondayGanttElement(task.element) && task.isIntegrated())
        );

      if (mondayItemsTasksForConnectors.length === 2) {
        const toItem = mondayItemsTasksForConnectors.find((task) => task.id === connector.to.id) as
          | IGanttBaseCellController<GanttMondayItemElement>
          | undefined;
        const fromItem = mondayItemsTasksForConnectors.find((task) => task.id === connector.from.id) as
          | IGanttBaseCellController<GanttMondayItemElement>
          | undefined;
        if (toItem && fromItem) {
          if (connector.type === "monday") {
            trackGanttEvent("gantt_connector_monday_popup_opened");
            setShowMondayDependenciesPopup(true);
            taskItemsToRemoveDependency.current.set(`${getMondayItemId(fromItem.element)}-${connector.id}`, toItem);
          } else {
            controller.removeConnector(connector.id);
          }
          return;
        }
      }
      controller.removeConnector(connector.id);
    },
    [controller]
  );

  const handleKeyboardShortcut = useCallback(
    (shortcut: CanvasKeyboardShortcut, e: KeyboardEvent) => {
      if (shortcut !== CanvasKeyboardShortcut.delete) {
        return;
      }

      const selectedConnectorId = controller.getSelectedConnectorId();
      if (selectedConnectorId) {
        const connector = controller.getConnectors().find((c) => c.id === selectedConnectorId);
        if (connector) {
          removeConnector(connector);
        }
        e.stopPropagation();
      } else if (controller.getSelectedSplitId() || controller.getSelectedRowId()) {
        controller.deleteSelectedElement();
        e.stopPropagation();
      }
    },
    [controller, removeConnector]
  );

  useKeyboardShortcuts(handleKeyboardShortcut, {
    enabled: controller.isSelected(),
    disablePropagation: false,
    capture: true,
  });

  const handleMouseMove = useCallback((e: Konva.KonvaEventObject<MouseEvent>) => {
    if (isTargetAnchor(e.target)) {
      // if the target is an anchor, do nothing
      return;
    }
    if (isTargetTaskCell(e.target)) {
      const id = e.target.attrs.id.slice(5);
      if (id) {
        hoverTaskCell(id, true);
      }
      return;
    }

    if (isTargetSplitCell(e.target)) {
      const [_, splitId, rowId] = e.target.attrs.id.split("-");
      return setIsHoveringSplitRow(`${splitId}-${rowId}`);
    }
    setIsHoveringSplitRow(null);
  }, []);

  function onMouseLeave() {
    setIsHoveringSplitRow(null);
  }

  function renderDateColumnHeader() {
    return controller.getDateColumnsLayout().map((column) => <GanttHeaderDateCell key={column.id} {...column} />);
  }

  function renderSplitColumns() {
    return controller
      .getSplitColumns()
      .sort((a, b) => (a.isSelected() ? 1 : b.isSelected() ? -1 : 0))
      .map((controller) => <GanttSplitColumn key={controller.getSplitId()} controller={controller} />);
  }

  function renderGridLayout() {
    const headerPositions = controller
      .getDateColumnsLayout()
      .filter((h) => h.isHeader)
      .slice(1, Number.POSITIVE_INFINITY);

    const { height } = controller.getLayoutRect();

    const shouldShowGuideLines = controller.element.granularity !== "day";

    return (
      <>
        {controller.getLayoutCells().map((cell) => {
          return (
            <Rect
              key={`grid-${cell.rowId}-${cell.date}${cell.x}${cell.y}`}
              x={cell.x}
              y={cell.y}
              width={cell.width}
              height={cell.height}
              fill={"white"}
              stroke={Constants.LayoutBorderColor}
              strokeWidth={Constants.LayoutBorderWidth}
              cornerRadius={getCornerRadius(cell.cornerRadius)}
            />
          );
        })}

        {shouldShowGuideLines &&
          headerPositions.map((column) => {
            return <Rect key={column.id} x={column.x - 1} y={column.y} width={2} height={height} stroke="#DADCE0" />;
          })}
      </>
    );
  }

  function getTaskCellsRefsMap() {
    if (!taskCellRefs.current) {
      // Initialize the Map on first usage.
      taskCellRefs.current = new Map();
    }
    return taskCellRefs.current;
  }

  function hoverTaskCell(id: string, isHovering: boolean) {
    const map = getTaskCellsRefsMap();
    for (const [key, value] of map.entries()) {
      if (key !== id) {
        value?.setIsHovering(false);
      }
    }
    map.get(id)?.setIsHovering(isHovering);
  }

  function renderTaskCells() {
    const tasks = controller.getTaskCells();
    if (tasks.length === 0) {
      const cells = controller.getLayoutCells();
      if (cells.length === 0) {
        return null;
      }
      const xPositions = cells.map((c) => c.x);
      const startX = Math.min(...xPositions);
      const endX = Math.max(...xPositions);

      const width = endX - startX + cells[0].width;
      return (
        <Group listening={false} x={startX} y={cells[0].y}>
          <EmptyStateTasks width={width} height={(cells.at(-2)?.y ?? 200) + 10} />
        </Group>
      );
    }
    return tasks.map((controller) => (
      <GanttTaskComponent
        key={controller.id}
        controller={controller}
        onDrag={onDrag}
        onDragStart={onDragStart}
        onDragEnd={onDragEnd}
        setMouseDragFunc={setMouseDragFunction}
        onResize={onResize}
        onHover={(id) => {
          setIsHoveringTask(id);
        }}
        ghostConnector={ghostConnector}
        ref={(node) => {
          const map = getTaskCellsRefsMap();
          map.set(controller.id, node);

          return () => {
            map.delete(controller.id);
          };
        }}
      />
    ));
  }

  const updateMondayItemDate = useCallback(
    (taskId: string, startDate?: number, endDate?: number) => {
      controller.context.reflect?.mutate.getElement(taskId).then((task) => {
        if (!task) {
          return;
        }
        const taskElement = task as IntegrationItem;
        // dont allow to drop if no config
        const config = taskElement && findExistingMapping(taskElement?.integrationId);
        if (config) {
          if (startDate) {
            updateItem(getMondayItemId(taskElement), taskElement?.integrationId, {
              columnValue: {
                id: config.start_date,
                value: addDays(cleanDate(new Date(startDate ?? 0)), 1),
                type: "date",
              },
            });
          }
          if (endDate) {
            updateItem(getMondayItemId(taskElement), taskElement?.integrationId, {
              columnValue: {
                id: config.end_date,
                value: addDays(cleanDate(new Date(endDate ?? 0)), 1),
                type: "date",
              },
            });
          }
        }
      });
    },
    [controller, findExistingMapping, updateItem]
  );

  const setMouseDragFunction = (e: Konva.KonvaEventObject<MouseEvent>) => {
    setMouseDrag(e);
    setIsDraggingTask(false);
  };

  function getPointForEvent(e: Konva.KonvaEventObject<DragEvent> | Konva.KonvaEventObject<MouseEvent>) {
    const stage = e.currentTarget.getStage();
    if (!stage) {
      return null;
    }
    const layoutRect = controller.getLayoutRect();

    const stagePosition = stage.getAbsolutePosition();
    const scale = stage.scaleX();

    const point = {
      x: (e.evt.offsetX - stagePosition.x) / scale,
      y: (e.evt.offsetY - stagePosition.y) / scale,
    };

    const internalPoint = {
      x: (point.x - layoutRect.x) / (controller.element.scaleX ?? 1),
      y: (point.y - layoutRect.y) / (controller.element.scaleY ?? 1),
    };

    return internalPoint;
  }

  const onDragStart = (id: string) => {
    setSelectedElementIds([id]);
  };
  const onDragEnd = (id: string) => {
    reportTaskDateChanged(
      isIdForMondayElement(id) ? "integrationItem" : id.startsWith("taskCard") ? "taskCard" : "task"
    );
    updateTaskDateChanged(id);
    setSelectedElementIds([]);
    setIsDraggingTask(false);
  };
  const onDrag = (id: string, e: Konva.KonvaEventObject<DragEvent>, point: { x: number; y: number }) => {
    if (!user) {
      controller.context.showErrorToast?.(<GuestUserError />);
      return;
    }
    setIsDraggingTask(true);
    e.cancelBubble = true;

    const internalPoint = {
      x: point.x / (controller.element.scaleX ?? 1),
      y: point.y / (controller.element.scaleY ?? 1),
    };
    handleDraggedTask(id, internalPoint, e);
  };

  const onResize =
    ({ id, x, y, side }: { id: string; x: number; y: number; side: "start" | "end" }) =>
    (e: Konva.KonvaEventObject<DragEvent>) => {
      if (!user && id.startsWith("integrationItem")) {
        if (!hasUserSeenGanttGuestUserError()) {
          controller.context.showErrorToast?.(<GuestUserError />);
        }
        setUserHasSeenGanttGuestUserError();
        return;
      }
      setIsDraggingTask(true);

      e.cancelBubble = true;
      const line = e.currentTarget;
      // reset line to original position
      line.y(y);
      line.x(x);

      const point = getPointForEvent(e);
      if (!point) {
        return;
      }

      // get the grid cell that the point is hovering over
      const cell = controller.getHoveredGridCell(point);
      if (cell) {
        const startDate = side === "start" ? cell.date : undefined;
        const endDate = side === "end" ? cell.date : undefined;
        controller.changeDateForTask(id, startDate?.getTime(), endDate?.getTime());
        updateMondayItemDate(id, startDate?.getTime(), endDate?.getTime());
        reportTaskDateChanged(
          isIdForMondayElement(id) ? "integrationItem" : id.startsWith("taskCard") ? "taskCard" : "task"
        );
      }
    };

  function renderHoveredTaskAnchors() {
    if (!isHoveringTask || controller.isReadOnly()) {
      return null;
    }
    const layout = controller.getTaskCellLayout(isHoveringTask);
    const task = controller.getTaskCells().find((task) => task.id === isHoveringTask);
    if (!layout) {
      return null;
    }

    const padding = 10;
    const lineWidth = 5;
    const y = layout.y + padding;
    const startX = layout.x + padding;
    const endX = layout.x + layout.width - padding - lineWidth;
    const connectorAnchorX = layout.x + layout.width - padding;
    const connectorAnchorY = layout.y + layout.height / 2;

    const onDragConnectorStart =
      (reversed = false) =>
      () => {
        if (!user && isHoveringTask.startsWith("integrationItem")) {
          if (!hasUserSeenGanttGuestUserError()) {
            controller.context.showErrorToast?.(<GuestUserError />);
          }
          setUserHasSeenGanttGuestUserError();
          return;
        }
        const from = { taskId: isHoveringTask, x: reversed ? startX : connectorAnchorX, y: connectorAnchorY };
        const to = { x: connectorAnchorX, y: connectorAnchorY };
        setGhostConnector({
          from: reversed ? to : from,
          to: reversed ? from : to,
        });
      };
    const onDragConnector =
      (reversed = false) =>
      (e: Konva.KonvaEventObject<DragEvent>) => {
        e.cancelBubble = true;
        const circle = e.currentTarget;
        // reset line to original position
        circle.y(0);
        circle.x(0);

        const point = getPointForEvent(e);
        if (!point) {
          return;
        }

        const task = controller.getHoveredTaskCell(point);
        if (task && task.elementId !== isHoveringTask) {
          const anchor = reversed
            ? {
                x: task.x + task.width - 10,
                y: task.y + task.height / 2,
              }
            : {
                x: task.x + 10,
                y: task.y + task.height / 2,
              };
          setGhostConnector((ghost) => ({
            ...ghost!,
            [reversed ? "from" : "to"]: { ...anchor, taskId: task.elementId },
          }));
        } else {
          setGhostConnector((ghost) => ({
            ...ghost!,
            [reversed ? "from" : "to"]: point,
          }));
        }
      };
    const onDragConnectorEnd = async (e: Konva.KonvaEventObject<DragEvent>) => {
      if (!ghostConnector?.from?.taskId || !ghostConnector?.to?.taskId) {
        setGhostConnector(null);
        return;
      }
      if (ghostConnector.from.taskId && ghostConnector.to.taskId) {
        trackGanttEvent("gantt_connector_added");
        const shouldHandleDependencies = getFeatureFlag("gantt-monday-dependency-column");
        if (shouldHandleDependencies) {
          const mondayItemsTasksForConnectors = controller
            .getTaskCells()
            .filter(
              (task) =>
                (task.id === ghostConnector.from.taskId && isMondayGanttElement(task.element) && task.isIntegrated()) ||
                (task.id === ghostConnector.to.taskId && isMondayGanttElement(task.element) && task.isIntegrated())
            );

          if (mondayItemsTasksForConnectors.length === 2) {
            const toItem = mondayItemsTasksForConnectors.find((task) => task.id === ghostConnector.to.taskId) as
              | IGanttBaseCellController<GanttMondayItemElement>
              | undefined;
            const fromItem = mondayItemsTasksForConnectors.find((task) => task.id === ghostConnector.from.taskId) as
              | IGanttBaseCellController<GanttMondayItemElement>
              | undefined;
            if (toItem && fromItem) {
              addItemsToQueue({ [toItem.element.integrationId]: [getMondayItemId(toItem.element)] });
              setGhostConnector(null);

              const newId = await controller.addConnector(
                ghostConnector.from.taskId,
                ghostConnector.to.taskId,
                "custom"
              );
              if (newId) {
                // make sure the connector is not added if the items are from different boards
                if (toItem.element.integrationId !== fromItem.element.integrationId) {
                  setGhostConnector(null);
                  controller.addConnector(ghostConnector.from.taskId, ghostConnector.to.taskId, "custom");
                } else {
                  const toItemConfig = toItem.getConfiguration?.();
                  if (toItemConfig && !toItemConfig.dependencyId) {
                    controller.context.showErrorToast?.(
                      <div>This monday item is not configured to have a dependency column.</div>
                    );
                    unsetMouse(e);
                    setMondayIntegrationPopup({
                      isOpen: true,
                      selectedResult: "map",
                      integrationId: toItem.element.integrationId,
                    });
                  } else {
                    setGhostConnector(null);
                    setConnectorPopup({
                      x: ghostConnector.to.x,
                      y: ghostConnector.to.y,
                      changeToMondayConnector: () => {
                        controller.changeConnectorType(newId, "monday");
                        toItem.addMondayIntegrationConnector?.(getMondayItemId(fromItem.element));
                      },
                    });
                  }
                }
              }
            }
          } else {
            setGhostConnector(null);
            controller.addConnector(ghostConnector.from.taskId, ghostConnector.to.taskId, "custom");
          }
        } else {
          setGhostConnector(null);
          controller.addConnector(ghostConnector.from.taskId, ghostConnector.to.taskId, "custom");
        }
      }
      setGhostConnector(null);
    };

    const startShift = 0;
    const endShift = 0;

    const hoverdDate = formatRelatedDates(task?.getStartDate() ?? 0, task?.getEndDate() ?? 0);

    const openTask = () => {
      if (isIdForMondayElement(isHoveringTask)) {
        if (task && isMondayGanttElement(task.element)) {
          window.open(task.getLink?.(), "_blank");
        }
      } else {
        if (task?.element.type === "taskCard") {
          setFocusedElementId(isHoveringTask);
        } else if (task?.element.type === "task") {
          showModalFromKonva(EditTaskModal, { taskId: task.id, context: controller.context });
        }
      }
    };

    const textElement = new Konva.Text({
      text: task?.getTitle(),
      fontFamily: consts.DEFAULTS.FONT,
      lineHeight: consts.LINE_HEIGHT,
      fontSize: 12,
      fontStyle: "300",
      textDecoration: "none",
      ellipsis: false,
      align: "left",
      verticalAlign: "top",
    });

    const resizeHitZone = 10;
    const isError = !!task?.getError?.();

    return (
      <Group
        name={"anchor"}
        visible={!isError}
        onMouseLeave={(e) => {
          const map = getTaskCellsRefsMap();
          const rect = map?.get(isHoveringTask)?.taskCellRef?.current?.getClientRect();
          if (rect) {
            const isInside = isPointInRect(
              {
                x: e.evt.clientX,
                y: e.evt.clientY,
              },
              rect
            );

            if (!isInside) {
              hoverTaskCell(isHoveringTask, false);
            }
          }
        }}
        onMouseEnter={() => {
          hoverTaskCell(isHoveringTask, true);
        }}
      >
        {[
          {
            x: startX + startShift,
            y: connectorAnchorY,
            side: "left" as "left" | "right",
          },
          {
            x: connectorAnchorX + endShift,
            y: connectorAnchorY,
            side: "right" as "left" | "right",
          },
        ].map(({ x, y, side }) => {
          if (!ref.current?.getClientRect()) {
            return null;
          }
          const viewportHeight = stage?.current?.getSize().height ?? 0;
          const height =
            layout.height - (padding * 2) / (controller.element.scaleY ?? 1) / (stage?.current?.scaleY() ?? 1);
          if (height / viewportHeight < 0.0002) {
            // If the node is too small compared the the viewport, don't render
            return null;
          }

          return (
            <Group
              x={x}
              y={y}
              scaleX={1 / (controller.element.scaleX ?? 1) / Math.min(1, stage?.current?.scaleX() ?? 1)}
              scaleY={1 / (controller.element.scaleY ?? 1) / Math.min(1, stage?.current?.scaleX() ?? 1)}
              key={side}
            >
              <Circle
                id={`anchor-connector-${side}`}
                fill={"#00A1FF"}
                radius={hoveringConnector === side ? 8 : 5}
                draggable
                onDragStart={onDragConnectorStart(side === "left")}
                onDragEnd={(e) => {
                  onDragConnectorEnd(e);
                  unsetMouse(e);
                }}
                onDragMove={(e) => {
                  onDragConnector(side === "left")(e);
                  setMousePointer(e);
                  e.cancelBubble = true;
                }}
                onMouseEnter={(e) => {
                  setHoveringConnector(side);
                  setMousePointer(e);
                  e.cancelBubble = true;
                }}
                onMouseLeave={(e) => {
                  setHoveringConnector(null);
                  unsetMouse(e);
                }}
                hitStrokeWidth={resizeHitZone}
              />

              {hoveringConnector === side && (
                <Path x={-5} y={-5} listening={false} stroke="#fff" data="M0 5h10M5 0v10" />
              )}
            </Group>
          );
        })}

        <Group
          x={connectorAnchorX - 15}
          y={connectorAnchorY - 30}
          onClick={() => {
            if (!task) return;
            trackGanttEvent("gantt_task_opend", {
              from: "chevron_click",
              type: task.element.type,
            });
            openTask();
          }}
          onMouseEnter={setMousePointer}
          id={`open-task-${isHoveringTask}`}
        >
          <Rect x={0} y={0} width={10} height={10} fill="transparent" id={`open-task-${isHoveringTask}`} />
        </Group>

        {isDraggingTask && (
          <KonvaTooltip y={y - 40} x={startX + lineWidth / 2} parentWidth={endX - startX} text={hoverdDate || ""} />
        )}

        {textElement.getWidth() >= layout.width - Constants.taskCellPadding * 4 - 28 &&
          (task?.getTitle() || "").length > 0 && (
            <KonvaTooltip x={startX} y={y - 35} parentWidth={endX - startX} text={task?.getTitle() || ""} />
          )}
      </Group>
    );
  }

  function renderGhostConnector() {
    if (!ghostConnector) {
      return null;
    }
    return (
      <SimpleConnector
        id={Constants.ConnectorPrefix}
        p1={{ ...ghostConnector.from, rotation: 0 as Degrees }}
        p2={{ ...ghostConnector.to, rotation: 180 as Degrees }}
        lineType={"curve"}
        element={{
          strokeWidth: 1,
          stroke: "#657E9A",
        }}
      />
    );
  }

  const resetSelection = (
    event?: Konva.KonvaEventObject<MouseEvent> | Konva.KonvaEventObject<DragEvent> | CustomEvent
  ) => {
    setSelectedElementIds((previous) => [...previous]);
    setTimeout(() => {
      setSelectedElementIds((previous) => [...previous]);
    }, 50);
    if (event) {
      resetPointer(event);
    }
  };

  const updateTransformerNodes = (taskId: string) => {
    const map = getTaskCellsRefsMap();
    const node = map.get(taskId)?.taskCellRef.current?.parent;
    hoverTaskCell(taskId, true);
    if (node && node?.attrs?.element?.type) {
      transformerRef?.current?.nodes([]);
    }
  };

  async function handleDraggedTask(
    taskId: string,
    point: { x: number; y: number },
    event: Konva.KonvaEventObject<MouseEvent> | Konva.KonvaEventObject<DragEvent> | CustomEvent
  ) {
    const oldTaskLayout = controller.getTaskCellLayout(taskId);
    const task = controller.getTaskCells().find((task) => task.id === taskId);
    const [customRowIntegrationId] = (controller.element.customRows?.id ?? "").split("_");

    let cell = controller.getHoveredGridCell(point);
    const ganttShouldKeep = "detail" in event && event.detail?.ganttShouldKeep;
    if (!cell && !ganttShouldKeep) {
      const layout = controller.getLayoutRect();

      let offsetX = 200;
      let offsetY = 50;

      const taskElement = task?.element;
      if (taskElement?.type === "task") {
        // For new task elements, use their actual width
        offsetX = (taskElement.width ?? 275) / 2;
        offsetY = (taskElement.height ?? 80) / 2;
      } else if (taskElement?.type === "taskCard") {
        // For task cards, use their standard width
        offsetX = 325 / 2; // CARD_WIDTH / 2
        offsetY = 50; // approximate height / 2
      }

      const position = {
        x: point.x * (controller.element.scaleX ?? 1) + layout.x - offsetX,
        y: point.y * (controller.element.scaleY ?? 1) + layout.y - offsetY,
      };
      controller.dragTasksOut(taskId, position).then((handled) => {
        if (handled) {
          const startDateColumnId = task?.getConfiguration?.()?.startId;
          if (task && isMondayGanttElement(task.element) && startDateColumnId && task?.getStartDate()) {
            const itemId = getMondayItemId(task.element);
            updateColumnValue(task.element.integrationId, itemId, startDateColumnId, {
              date: addDays(cleanDate(new Date(task?.getStartDate() ?? 0)), 1),
            });
          }

          resetSelection(event);
        }
      });
      return;
    } else if (ganttShouldKeep) {
      cell = controller.getLayoutCells()[0];
    }
    if (!cell) {
      return;
    }
    setTimeout(() => transformerRef?.current?.update(), 0);

    // when dropping a new task, check if we're allowed to add it
    const isDropEvent = "type" in event && event.type === EVT_ELEMENT_DROP;
    if (isDropEvent) {
      if (currentDragInfo.current) {
        if (currentDragInfo.current.taskId === taskId && !currentDragInfo.current.canAddNewTask) {
          const layout = controller.getLayoutRect();
          const position = {
            x: point.x * (controller.element.scaleX ?? 1) + layout.x - 200,
            y: point.y * (controller.element.scaleY ?? 1) + layout.y - 50,
          };
          controller.dragTasksOut(taskId, position).then((handled) => {
            if (handled) {
              resetSelection(event);
            }
          });
          controller.context?.showUpgradeModal?.("gantt");
          return;
        }
      }
    }

    const taskElement = (await controller.context.reflect?.mutate.getElement(taskId)) as IntegrationItem;
    const config = taskElement && findExistingMapping(taskElement.integrationId);
    const hasDateConfig = hasDateConfigInMetadata(config || {});
    // not allow to move task from different integration in custom row mode
    if (
      controller.isCustomRowSplitMode() &&
      customRowIntegrationId !== taskElement?.integrationId &&
      task?.getLayout()?.rowId !== cell?.rowId
    ) {
      const lastSplit = controller.element.splits.at(-1);
      if (lastSplit) {
        const lastRow = lastSplit.rows.at(-1);
        if (lastRow) {
          await controller.dropElements(lastRow.id, { startDate: cell.date }, taskId);
          updateTransformerNodes(taskId);
        }
      }
      return;
    }

    if (!user && isMondayGanttElement(taskElement)) {
      if (!hasUserSeenGanttGuestUserError()) {
        controller.context.showErrorToast?.(<GuestUserError />);
      }
      setUserHasSeenGanttGuestUserError();
      return;
    }

    const userHasIntegration = tasksIntegrations?.find(
      (t) => isMondayGanttElement(taskElement) && taskElement.integrationId === t.id
    );
    // new task that has no config
    if (!hasDateConfig && isMondayGanttElement(taskElement) && !userHasIntegration) {
      controller.context.showErrorToast?.("You don't have permission to set Gantt integration settings for this item.");
      return;
    } else if (!task && !hasDateConfig && isMondayGanttElement(taskElement) && userHasIntegration) {
      setMondayIntegrationPopup({
        isOpen: true,
        selectedResult: "map",
        integrationId: taskElement?.integrationId,
      });
      return;
    } else if (task && !hasDateConfig && isMondayGanttElement(taskElement)) {
      // new task that has config
      await controller.dropElements(cell.rowId, { startDate: cell.date }, taskId);
      updateTransformerNodes(taskId);
      setMondayIntegrationPopup({
        isOpen: true,
        selectedResult: "map",
        integrationId: taskElement?.integrationId,
      });
      return;
    }

    let dropResult: { didDrop: boolean; reason?: string } | null = null;
    const canAddNewTask = await controller.canAddNewTask();
    if (isMondayGanttElement(taskElement)) {
      const dates = getIntegrationTaskDates(taskElement);
      if (dates) {
        const diff = dates.endDate - dates.startDate;
        dropResult =
          (await controller.dropElements(
            cell.rowId,
            {
              startDate: cell.date,
              endDate: new Date(new Date(cell.date).getTime() + diff),
            },
            taskId
          )) || null;
        updateTransformerNodes(taskId);
      }
    } else {
      dropResult = (await controller.dropElements(cell.rowId, { startDate: cell.date }, taskId)) || null;
      updateTransformerNodes(taskId);
    }
    // all ok
    if (dropResult?.didDrop) {
      if (currentDragInfo.current === null || currentDragInfo.current.taskId !== taskId) {
        currentDragInfo.current = { taskId, canAddNewTask };
      }
      updateMondayItemDate(
        taskId,
        new Date(task?.getStartDate() ?? 0).getTime(),
        new Date(task?.getEndDate() ?? 0).getTime()
      );
    }

    if (dropResult && !dropResult.didDrop && dropResult?.reason === "duplicate-monday") {
      controller.context.showErrorToast?.("This monday item is already placed in the gantt chart.");
      resetSelection(event);
      return;
    }
    const newTaskColumnValue = controller.element.splits.flatMap((s) => s.rows).find((r) => r.id === cell?.rowId);
    if (newTaskColumnValue && controller.isCustomRowSplitMode() && oldTaskLayout?.rowId !== cell.rowId) {
      changeTaskColumnValue?.[taskId]?.(newTaskColumnValue.title);
    }
  }

  function renderTaskConnectors() {
    const connectors = controller.getConnectors();
    const tasks = controller.getTaskCells();
    return connectors.map((connector) => {
      const relevantTasks = tasks.filter((task) => task.id === connector.from.id || task.id === connector.to.id);
      const someAreLoading = relevantTasks.some((task) => task?.isInitialLoading?.());
      if (someAreLoading) {
        return null;
      }
      const isSelected = controller.getSelectedConnectorId() === connector.id;

      return (
        <GanttConnectorWrapper
          isSelected={isSelected}
          tasks={tasks}
          connector={connector}
          key={connector.id}
          id={`${Constants.ConnectorPrefix}-${connector.id}`}
          p1={{ ...connector.from, rotation: 0 as Degrees }}
          p2={{ ...connector.to, rotation: 180 as Degrees }}
          lineType={"curve"}
          element={{
            strokeWidth: 1,
            stroke: isSelected ? "#00A1FF" : "#657E9A",
            hitStrokeWidth: 10,
          }}
          bboxPosition={{ x: controller.getLayoutRect().x, y: controller.getLayoutRect().y }}
          removeConnector={() => removeConnector(connector)}
        />
      );
    });
  }

  async function onDblClick(e: Konva.KonvaEventObject<MouseEvent>) {
    if (controller.isReadOnly()) {
      return;
    }
    const point = getPointForEvent(e);

    if (!point) {
      return;
    }

    const gridCell = controller.getHoveredGridCell(point);
    if (!gridCell) {
      return;
    }
    const existingTask = controller.getHoveredTaskCell(point);
    if (existingTask) {
      // if there is an existing task, we should not create a new one
      return;
    }
    const didCreateTask = await controller.createTask(gridCell.rowId, gridCell.date);
    if (didCreateTask) {
      trackGanttEvent("gantt_task_created", {
        from: "db_click",
        amount: controller.getTaskCells().length + 1,
        type: "taskCard",
      });
    }
  }

  function onClick(e: Konva.KonvaEventObject<MouseEvent>) {
    if (controller.isReadOnly()) {
      return;
    }

    if (isTargetConnector(e.target)) {
      const [, connectorId] = (e.target?.attrs?.id?.split(`${Constants.ConnectorPrefix}-`) ?? []) as string[];
      controller.setSelectedConnector(connectorId);
    } else {
      controller.setSelectedConnector(null);
    }

    if (!isTargetAnchor(e.target) && !isHoveringSplitRow) {
      controller.setSelectedColumnRow(null, null);
    }
  }

  function renderAddBelowRow(
    splitId: string,
    rowId: string,
    splitCellController: IGanttSplitCellController | undefined
  ) {
    const cellLayout = controller.getCellLayout(splitId, rowId);
    if (!cellLayout || !splitCellController || isPresenting) {
      return null;
    }

    const y = cellLayout.y + cellLayout.height;
    const x = cellLayout.x + cellLayout.width / 2;

    return (
      <Group
        x={x}
        y={y}
        onClick={() => {
          splitCellController.addRow();
          trackGanttEvent("gantt_row_added", {
            from: "circle_button",
          });
        }}
        id={"anchor-add-below-column"}
        key={"anchor-add-below-column"}
      >
        <AddButton width={18} height={18} hitHeight={30} hitWidth={30}>
          <KonvaTooltip y={-45} x={-10} parentWidth={20} text={"Add row"} />
        </AddButton>
      </Group>
    );
  }

  function renderAddRightColumn(
    splitId: string,
    rowId: string,
    splitCellController: IGanttSplitCellController | undefined
  ) {
    const cellLayout = controller.getCellLayout(splitId, rowId);
    if (!cellLayout || !splitCellController) {
      return null;
    }

    const x = cellLayout.x + cellLayout.width;
    const y = cellLayout.y + cellLayout.height / 2;

    return (
      <Group
        x={x}
        y={y}
        onClick={() => {
          splitCellController.addColumn();
          trackGanttEvent("gantt_column_added", {
            from: "circle_button",
          });
        }}
        id={"anchor-add-right-column"}
        key={"anchor-add-right-column"}
      >
        <AddButton width={18} height={18} hitHeight={30} hitWidth={30}>
          <KonvaTooltip y={-45} x={-10} parentWidth={20} text={"Add Column"} />
        </AddButton>
      </Group>
    );
  }

  function renderTitlePlaceholder(splitId: string, rowId: string) {
    const selectedRowId = controller.getSelectedRowId();
    const cellLayout = controller.getCellLayout(splitId, rowId);

    if (!cellLayout || cellLayout?.title || selectedRowId === rowId) {
      return null;
    }

    const x = cellLayout.x + 21;
    const y = cellLayout.y + cellLayout.height / 2 - 7;

    return (
      <Group x={x} y={y} id={"anchor-add-text"} key={"anchor-add-text"}>
        <Text
          text={"Add text"}
          align={"center"}
          verticalAlign={"middle"}
          fontFamily={"Poppins"}
          fontSize={14}
          fill={"#676879"}
          listening={false}
        />
      </Group>
    );
  }

  function renderAddButtons() {
    if (!isHoveringSplitRow) {
      return null;
    }

    const [splitId, rowId] = isHoveringSplitRow.split("-");
    const splitCellController = controller.getSplitCells(splitId).find((cell) => cell.getRowId() === rowId);

    return (
      <Group>
        {controller.canAddSplitRowBelow(splitId) && renderAddBelowRow(splitId, rowId, splitCellController)}
        {controller.canAddSplitColumnRight() && renderAddRightColumn(splitId, rowId, splitCellController)}
        {renderTitlePlaceholder(splitId, rowId)}
      </Group>
    );
  }

  function renderSelectedRow() {
    const selectedSplitId = controller.getSelectedSplitId();
    const selectedRowId = controller.getSelectedRowId();
    if (!selectedSplitId || !selectedRowId) {
      return null;
    }
    const row = controller.getSplitCells(selectedSplitId).find((cell) => cell.getRowId() === selectedRowId);
    if (!row) {
      return null;
    }
    const rect = row.getRect();
    const { width } = controller.getLayoutRect();
    return (
      <Rect
        {...rect}
        width={width - rect.x}
        fill="transparent"
        stroke={Constants.SelectedCellBorderColor}
        strokeWidth={2}
        listening={false}
        cornerRadius={getCornerRadius(rect.cornerRadius)}
      />
    );
  }

  return (
    <Group
      ref={ref}
      onMouseMove={handleMouseMove}
      onMouseLeave={onMouseLeave}
      listening={true}
      id={"gantt-parent-node"}
      onDblClick={onDblClick}
      onClick={onClick}
    >
      {renderDateColumnHeader()}
      {renderGridLayout()}
      {renderSplitColumns()}
      {renderTaskConnectors()}
      {renderTaskCells()}
      {renderHoveredTaskAnchors()}
      {renderAddButtons()}
      {renderGhostConnector()}
      {renderSelectedRow()}
      {!controller.isReadOnly() && (
        <AddActions x={controller.getDateColumnsLayout()?.at(0)?.x ?? 0} y={-40} controller={controller} />
      )}
      <ConnectorPopup
        x={connectorPopup?.x}
        y={connectorPopup?.y}
        onChange={(option) => {
          if (option === "custom") {
            setConnectorPopup(null);
            return;
          }
          if (option === "monday") {
            connectorPopup?.changeToMondayConnector();
          }
          setConnectorPopup(null);
        }}
        onClose={() => setConnectorPopup(null)}
      />
      {showMondayDependenciesPopup && (
        <Html transform={false}>
          <Modal
            dimBackground
            onClickBackground={() => {
              setShowMondayDependenciesPopup(false);
            }}
          >
            <RemoveMondayConnectorPopup
              onClose={() => {
                setShowMondayDependenciesPopup(false);
              }}
              onSelection={(shouldDelete) => {
                trackGanttEvent("gantt_connector_removed_monday_dependency", {
                  shouldDeleteInMonday: shouldDelete,
                });
                setShowMondayDependenciesPopup(false);
                if (shouldDelete) {
                  for (const [id, item] of taskItemsToRemoveDependency.current.entries()) {
                    const [itemId, connectorId] = (id ?? "").split("-");
                    if (itemId && connectorId) {
                      item.removeMondayIntegrationConnector?.(itemId).then(() => {
                        taskItemsToRemoveDependency.current.delete(itemId);
                      });
                      controller.removeConnector(connectorId);
                    }
                  }
                }
              }}
            />
          </Modal>
        </Html>
      )}
    </Group>
  );
}
